
import React, { useRef, useState } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start typing...',
  minHeight = '150px'
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [editorContent, setEditorContent] = useState(value || "");

  const formatText = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    updateContent();
  };

  const updateContent = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const toolbarButtons = [
    { command: 'bold', label: 'Bold', symbol: 'B', className: 'font-bold' },
    { command: 'italic', label: 'Italic', symbol: 'I', className: 'italic' },
    { command: 'underline', label: 'Underline', symbol: 'U', className: 'underline' }
  ];

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="border-b border-gray-200 bg-gray-50 p-2 flex space-x-1">
        {toolbarButtons.map((button) => (
          <button
            key={button.command}
            type="button"
            onClick={() => formatText(button.command)}
            className={`px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 transition-colors duration-200 ${button.className}`}
            title={button.label}
            aria-label={button.label}
          >
            {button.symbol}
          </button>
        ))}
      </div>

      {/* Editor */}
      <div
    ref={editorRef}
    contentEditable
    className={`p-4 outline-none ${minHeight ? `min-h-[${minHeight}]` : 'min-h-[150px]'} ${
      isFocused ? 'ring-2 ring-blue-500' : ''
    }`}
    style={{ minHeight }}
    onInput={updateContent}
    onFocus={() => setIsFocused(true)}
    onBlur={() => setIsFocused(false)}
    onPaste={handlePaste}
    role="textbox"
    aria-multiline="true"
    aria-label="Rich text editor"
  >
    {editorContent}
  </div>

      {!editorContent && !isFocused && (
    <div
      className="absolute top-16 left-4 text-gray-400 pointer-events-none"
      style={{ top: '56px' }}
    >
      {placeholder}
    </div>
  )}
    </div>
  );
};

export default RichTextEditor;
